"use client";

import { analyzeIdeaAction } from "@/app/actions/analyze-idea";
import { useAuth } from "@/context/AuthContext";
import { useValidator } from "@/context/ValidatorContext";
import { checkDailyLimit, incrementDailyLimit } from "@/lib/limit";
import { Idea, IdeaService } from "@/services/IdeaService";
import { Loader2, Sparkles } from "lucide-react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import RecentIdeas from "./RecentIdeas";

import { AnimatePresence, motion } from "framer-motion";

const FEEDBACK_MESSAGES = [
    "Great start! 🚀 Add a few more details (10+ words) for a better analysis.",
    "Your idea sounds interesting! Can you elaborate a bit more? (10+ words)",
    "Almost there! A few more details will help us give you a pro analysis. 💡",
    "Tell us more! Specifics help the AI understand your vision effectively.",
    "Keep going! ✍️ A little more description (10+ words) goes a long way.",
    "You're on to something! Add some features or goals to hit 10 words.",
    "Don't be shy! The more you type, the sharper the feedback. 🎯",
    "Brief is good, but detail is better! 10 words minimum for best results.",
    "Help us help you! 🤝 A bit more context (10+ words) ensures accuracy.",
    "Flesh it out! What problem does it solve? (Aim for 10+ words)",
    "Sounds cool! Who is it for? Add those details to reach 10 words.",
    "Just a little more! 🤏 10 words helps us roast (or toast) your idea properly.",
    "Expand on that thought! 🧠 The AI needs at least 10 words to work its magic.",
    "Details matter! 📝 Add a sentence or two to hit the 10-word mark.",
    "Paint the picture! 🎨 A richer description (10+ words) yields a smarter strategy."
];

const FormContent = ({
    idea,
    setIdea,
    placeholder,
    loading,
    handleSubmit,
    small,
    error,
    onFocusChange,
    iconOnlyButton = true,
    isWebMcpExposed = false
}: {
    idea: string,
    setIdea: (val: string) => void,
    placeholder: string,
    loading: boolean,
    handleSubmit: (e: any) => void,
    small?: boolean,
    error?: string,
    onFocusChange?: (isFocused: boolean) => void,
    iconOnlyButton?: boolean,
    isWebMcpExposed?: boolean
}) => {
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        onFocusChange?.(isFocused);
    }, [isFocused, onFocusChange]);

    const wordCount = idea.trim().split(/\s+/).filter(Boolean).length;

    if (small) {
        return (
            <form
                onSubmit={handleSubmit}
                className="relative group w-full"
                toolname={isWebMcpExposed ? "validate-startup-idea" : undefined}
                tooldescription={isWebMcpExposed ? "Validates a startup or product idea by checking its market feasibility, estimating development timeline and cost, and identifying key technical risks." : undefined}
                toolautosubmit={isWebMcpExposed ? "true" : undefined}
            >
                <AnimatePresence>
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            className="absolute bottom-full mb-3 left-0 right-0 mx-auto w-max max-w-[90%] text-center text-sm font-medium text-gray-600 bg-white/90 backdrop-blur-md px-4 py-2 rounded-full shadow-lg border border-gray-100/80"
                        >
                            {error}
                        </motion.div>
                    )}
                </AnimatePresence>
                <div className={`relative bg-white/80 backdrop-blur-xl rounded-full border transition-all duration-300 overflow-hidden
                    ${isFocused
                        ? 'border-orange-400/50 shadow-xl ring-2 ring-orange-100/50'
                        : 'border-neutral-200/80 shadow-md hover:shadow-lg hover:border-neutral-300'
                    }
                `}>
                    <input
                        type="text"
                        name="idea"
                        toolparamdescription={isWebMcpExposed ? "Detailed description of the startup or app idea to analyze." : undefined}
                        value={idea}
                        onChange={(e) => setIdea(e.target.value)}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        placeholder={placeholder}
                        disabled={loading}
                        data-gramm="false"
                        required
                        className={`w-full bg-transparent border-none outline-none pl-6 py-4 text-base text-gray-800 placeholder:text-gray-400 ${iconOnlyButton ? "pr-14" : "pr-14 md:pr-36"
                            }`}
                    />
                    <button
                        type="submit"
                        disabled={loading}
                        aria-label={loading ? "Analyzing..." : "Validate Idea"}
                        className={`absolute right-2 bg-black text-white rounded-full hover:scale-105 transition-all disabled:opacity-50 disabled:hover:scale-100 from-neutral-800 to-black bg-gradient-to-br shadow-md flex items-center justify-center gap-1.5
                            ${iconOnlyButton
                                ? "top-1/2 -translate-y-1/2 p-2"
                                : "top-1/2 -translate-y-1/2 p-2 md:top-2 md:bottom-2 md:translate-y-0 md:px-5 text-xs font-semibold"
                            }
                        `}
                    >
                        {loading ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                {!iconOnlyButton && <span className="hidden md:inline">Analyzing...</span>}
                            </>
                        ) : (
                            <>
                                {!iconOnlyButton && <span className="hidden md:inline">Validate Idea</span>}
                                <Sparkles className="h-4 w-4" />
                            </>
                        )}
                    </button>
                </div>
            </form>
        );
    }

    return (
        <form
            onSubmit={handleSubmit}
            className="relative w-full"
            toolname={isWebMcpExposed ? "validate-startup-idea" : undefined}
            tooldescription={isWebMcpExposed ? "Validates a startup or product idea by checking its market feasibility, estimating development timeline and cost, and identifying key technical risks." : undefined}
            toolautosubmit={isWebMcpExposed ? "true" : undefined}
        >
            <div className={`relative bg-white rounded-3xl border transition-all duration-200 overflow-hidden group
              ${isFocused
                    ? 'border-neutral-900 ring-1 ring-neutral-900/10'
                    : 'border-neutral-200 hover:border-neutral-300'
                }
            `}>
                <textarea
                    name="idea"
                    toolparamdescription={isWebMcpExposed ? "Detailed description of the startup or app idea to analyze." : undefined}
                    value={idea}
                    onChange={(e) => setIdea(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    placeholder="Describe your idea..."
                    data-gramm="false"
                    required
                    className="w-full h-32 md:h-40 p-6 md:p-8 text-lg md:text-xl bg-transparent border-none outline-none resize-none placeholder:text-neutral-400 text-neutral-800 font-sans"
                />

                {/* Dynamic word count badge inside text box (bottom-right) */}
                {idea.length > 0 && (
                    <div className="absolute bottom-3 right-4 flex items-center gap-1.5 pointer-events-none">
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border transition-all duration-200 ${wordCount >= 10
                                ? 'bg-neutral-50 text-neutral-800 border-neutral-200/60'
                                : 'bg-amber-50 text-amber-800 border-amber-200/60'
                            }`}>
                            {wordCount} / 10 words
                        </span>
                    </div>
                )}
            </div>

            <div className="flex items-center mt-4 min-h-[48px]">
                <AnimatePresence>
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            className="mr-4"
                        >
                            <span className="inline-block px-4 py-2 bg-neutral-50 text-neutral-600 text-sm font-semibold rounded-full border border-neutral-100">
                                {error}
                            </span>
                        </motion.div>
                    )}
                </AnimatePresence>
                <button
                    type="submit"
                    disabled={loading}
                    className={`
                    ml-auto relative h-12 px-6 rounded-xl font-bold text-sm transition-all duration-200 flex items-center gap-2
                    ${loading
                            ? "bg-neutral-100 text-neutral-400 cursor-not-allowed"
                            : "bg-neutral-900 text-white hover:bg-black"
                        }
                  `}
                >
                    {loading ? (
                        <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Analyzing...</span>
                        </>
                    ) : (
                        <>
                            <span>Validate Idea</span>
                            <Sparkles className="h-4 w-4" />
                        </>
                    )}
                </button>
            </div>
        </form>
    );
};

export default function IdeaValidator({
    className,
    initialPlaceholder = "Validate your concept...",
    mode = "combined",
    scrollThreshold = 300,
    showRecent = true,
    compact = false,
}: {
    className?: string,
    initialPlaceholder?: string,
    mode?: "combined" | "relative" | "sticky",
    scrollThreshold?: number,
    showRecent?: boolean,
    compact?: boolean,
}) {
    const { ideaInput: idea, setIdeaInput: setIdea, refreshIdeas, setCurrentIdea, isCookieBannerVisible } = useValidator();
    const [loading, setLoading] = useState(false);
    const [isSticky, setIsSticky] = useState(false);
    const [isStickyFocused, setIsStickyFocused] = useState(false);
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const { user, openLoginModal } = useAuth();

    // Auto-submit from query param when on the validator page
    useEffect(() => {
        if (pathname === "/ai-validator") {
            const ideaParam = searchParams.get("idea");
            if (ideaParam) {
                setIdea(ideaParam);
                // Clear query param by pushing to path without query
                router.replace("/ai-validator");
                analyzeIdea(ideaParam);
            }
        }
    }, [pathname, searchParams]);

    // Fix: Add mounted state to prevent hydration mismatch with localStorage
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);

    useEffect(() => {
        if (mode === 'relative') return; // Don't listen to scroll in relative mode

        const handleScroll = () => {
            setIsSticky(window.scrollY > scrollThreshold);
        };

        window.addEventListener("scroll", handleScroll);
        // Trigger once to set initial state
        handleScroll();

        return () => window.removeEventListener("scroll", handleScroll);
    }, [mode, scrollThreshold]);

    const analyzeIdea = async (ideaText: string) => {
        // If we are not on the validator page, redirect to the validator page with the idea in query params
        if (pathname !== "/ai-validator") {
            router.push(`/ai-validator?idea=${encodeURIComponent(ideaText)}`);
            return;
        }

        if (!user && !checkDailyLimit()) {
            openLoginModal();
            return;
        }

        setLoading(true);
        try {
            const data = await analyzeIdeaAction(ideaText);
            incrementDailyLimit();

            // Create New Idea Object
            const newIdea: Idea = {
                id: crypto.randomUUID(),
                description: ideaText,
                createdAt: Date.now(),
                lastUpdated: Date.now(),
                history: [{
                    prompt: "Initial Analysis",
                    result: data,
                    timestamp: Date.now()
                }]
            };

            // Save via Service
            await IdeaService.saveIdea(user ? user.uid : null, newIdea);
            await refreshIdeas();

            // Explicitly set as current idea to ensure ResultPage has data immediately
            setCurrentIdea(newIdea); // <--- ADDED THIS

            // Clear input and redirect with ID
            setIdea("");
            router.push(`/ai-validator/result?id=${newIdea.id}`);
        } catch (error) {
            console.error("Analysis failed", error);
            setLoading(false);
        }
    };

    const [placeholder, setPlaceholder] = useState("");

    // Animation logic
    useEffect(() => {
        const examples = [
            "A Tinder for adopting pets",
            "Uber for house cleaning",
            "AI-powered personal nutritionist",
            "Blockchain voting system",
            "Marketplace for used textbooks"
        ];

        // Start with the initial message
        setPlaceholder(initialPlaceholder);

        let currentExampleIndex = 0;
        let charIndex = 0;
        let isDeleting = false;
        let isWaiting = true;

        // Initial delay before starting animation
        const initialTimeout = setTimeout(() => {
            isWaiting = false;
        }, 2000);

        const interval = setInterval(() => {
            if (isWaiting) return;

            const currentExample = examples[currentExampleIndex];

            if (isDeleting) {
                // Deleting text
                setPlaceholder(current => current.substring(0, current.length - 1));
                if (placeholder.length === 0) {
                    isDeleting = false;
                    currentExampleIndex = (currentExampleIndex + 1) % examples.length;
                    charIndex = 0;
                }
            } else {
                // Typing text
                setPlaceholder(currentExample.substring(0, charIndex + 1));
                charIndex++;

                if (charIndex === currentExample.length) {
                    isDeleting = true;
                    isWaiting = true;
                    setTimeout(() => {
                        isWaiting = false;
                    }, 2000); // Wait 2s before deleting
                }
            }
        }, 50); // Typing speed

        return () => {
            clearTimeout(initialTimeout);
            clearInterval(interval);
        };
    }, [initialPlaceholder]);

    const [error, setError] = useState("");
    const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleSetIdea = (val: string) => {
        setIdea(val);
        if (error) {
            setError("");
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current);
            }
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const isAgent = (e as any).agentInvoked || (e.nativeEvent as any)?.agentInvoked;
        if (isAgent) {
            const promise = (async () => {
                const result = await analyzeIdeaAction(idea);
                return { success: true, data: result };
            })();
            const respond = (e as any).respondWith || (e.nativeEvent as any)?.respondWith;
            if (typeof respond === "function") {
                respond.call(e.nativeEvent || e, promise);
            }
            return;
        }

        const wordCount = idea.trim().split(/\s+/).filter(Boolean).length;

        if (wordCount < 10) {
            // Clear any existing timeout to reset the 5s timer
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current);
            }

            const randomMessage = FEEDBACK_MESSAGES[Math.floor(Math.random() * FEEDBACK_MESSAGES.length)];
            setError(randomMessage);

            errorTimeoutRef.current = setTimeout(() => {
                setError("");
            }, 5000);
            return;
        }

        import("@microsoft/clarity").then((clarityModule) => {
            clarityModule.default.event('lead_magnet_validate_idea');
        });
        analyzeIdea(idea);
    };

    return (
        <div className={`w-full ${className ?? "max-w-lg mt-8"}`}>
            {/* 1. Relative / Static Version (Always present in flow, hidden when sticky if desired, or just stays there) */}
            {(mode === "combined" || mode === "relative") && (
                <div className="opacity-100">
                    <div className="max-w-2xl mx-auto w-full">
                        <FormContent
                            idea={idea}
                            setIdea={handleSetIdea}
                            placeholder={placeholder}
                            loading={loading}
                            handleSubmit={handleSubmit}
                            small={compact || false}
                            error={error}
                            iconOnlyButton={false}
                            isWebMcpExposed={true}
                        />
                    </div>

                    {/* Previous Ideas List */}
                    {!user && showRecent && (
                        <div className="mt-6 text-center animate-in fade-in slide-in-from-bottom-4 duration-700 delay-150 max-w-2xl mx-auto">
                            <p className="text-gray-500 text-sm">
                                Want to save your analysis?{" "}
                                <button
                                    onClick={() => {
                                        import("@microsoft/clarity").then((clarityModule) => {
                                            clarityModule.default.event('lead_magnet_signin');
                                        });
                                        openLoginModal();
                                    }}
                                    className="font-semibold text-black hover:underline"
                                >
                                    Log in
                                </button>
                            </p>
                        </div>
                    )}

                    {showRecent && (
                        <div className="mt-8 flex justify-center w-full">
                            <RecentIdeas />
                        </div>
                    )}
                </div>
            )}

            {/* 2. Sticky / Fixed Version (Slides up) */}
            <AnimatePresence>
                {(isSticky && (mode === "combined" || mode === "sticky") && !isCookieBannerVisible) && (
                    <motion.div
                        initial={{ y: 100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 100, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 300, damping: 25 }}
                        className={`fixed bottom-6 left-0 right-0 mx-auto z-50 w-[90%] transition-all duration-300 ${isStickyFocused ? "max-w-2xl" : "max-w-md"} shadow-2xl rounded-full`}
                    >
                        <FormContent
                            idea={idea}
                            setIdea={handleSetIdea}
                            placeholder={placeholder}
                            loading={loading}
                            handleSubmit={handleSubmit}
                            small={true}
                            error={error}
                            onFocusChange={setIsStickyFocused}
                            iconOnlyButton={true}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
